/**
 * useArchiveApi - Unified Archive API Hook
 *
 * Automatically routes archive operations to the correct backend:
 * - Electron + connected: Local PostgreSQL via IPC
 * - Web/Cloud: HTTP API to Cloudflare Workers
 *
 * Components use this single hook without needing to know about platform.
 *
 * IMPORTANT: This hook is carefully designed to return STABLE references.
 * The returned object is memoized and callbacks only change when necessary.
 *
 * @module @humanizer/studio/hooks/useArchiveApi
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useArchiveOptional, type ArchiveProvider as IArchiveProvider } from '../contexts/ArchiveContext';
import type { ArchiveSource, ArchiveThread, ArchiveNode, ArchiveStats } from '../contexts/ApiContext';

/** Search result type */
export interface SearchResult {
  id: string;
  text: string;
  source: 'archive' | 'books';
  score: number;
  title?: string;
  sourceType?: string;
  wordCount: number;
  hierarchyLevel: number;
  scoreBreakdown?: {
    denseScore?: number;
    sparseScore?: number;
    fusedScore?: number;
    finalScore?: number;
  };
  provenance?: {
    sourceStore: 'archive' | 'books';
    sourceType: string;
    threadRootId?: string;
    threadTitle?: string;
    authorRole?: string;
    uri?: string;
  };
  enrichment?: {
    title?: string;
    summary?: string;
    rating?: number;
    categories?: string[];
    keyTerms?: string[];
    enrichedAt?: number;
  };
}

/**
 * Unified archive API interface
 */
export interface ArchiveApi {
  /** Whether the archive is available (connected) */
  isAvailable: boolean;

  /** Whether using local provider (Electron) vs cloud */
  isLocal: boolean;

  /** Get source type counts */
  getSources(): Promise<{ sources: ArchiveSource[]; total: number }>;

  /** Get thread list with filtering */
  getThreads(options?: {
    sourceType?: string;
    limit?: number;
    offset?: number;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: 'quality' | 'date';
    hideNoise?: boolean;
    minWords?: number;
    excludeFormats?: string[];
    onlyFormats?: string[];
    hasMedia?: boolean | null;
  }): Promise<{ threads: ArchiveThread[]; total: number; hasMore: boolean }>;

  /** Get content format profile for filter pills */
  getContentProfile(): Promise<{ formats: Record<string, number>; hasMedia: number; total: number }>;

  /** Server-side proximity-weighted search for browse panel */
  searchBrowse(query: string, options?: {
    limit?: number;
    sourceTypes?: string[];
    sourceTypeExclude?: string[];
    contextNodeIds?: string[];
    minWords?: number;
    excludeFormats?: string[];
    onlyFormats?: string[];
    hasMedia?: boolean | null;
  }): Promise<{ results: ArchiveThread[]; total: number }>;

  /** Get a single thread with all nodes */
  getThread(threadId: string): Promise<{ threadId: string; nodes: ArchiveNode[]; count: number }>;

  /** Get a single node by ID */
  getNode(nodeId: string): Promise<{ node: ArchiveNode }>;

  /** Get archive statistics */
  getStats(): Promise<ArchiveStats>;

  /** Keyword search (text search) */
  search(sessionId: string, query: string, options?: { sources?: string[]; limit?: number }): Promise<{ results: SearchResult[] }>;

  /** Semantic search (embedding-based) */
  searchSemantic(query: string, options?: { limit?: number; threshold?: number; sourceTypes?: string[] }): Promise<{ results: SearchResult[]; count: number }>;

  /** Find similar content by text */
  findSimilar(text: string, options?: { limit?: number; threshold?: number }): Promise<{ results: SearchResult[]; count: number }>;

  /** Hybrid search (keyword + semantic via agentic search) */
  searchHybrid(query: string, options?: { limit?: number; sourceTypes?: string[]; mode?: string }): Promise<{ results: SearchResult[]; count: number; stats?: any }>;

  /** Get a random embedded node for drift */
  randomNode(): Promise<{
    id: string;
    text: string;
    title?: string;
    sourceType: string;
    sourceCreatedAt?: string;
    wordCount: number;
    threadRootId?: string;
    authorRole?: string;
  } | null>;

  /** List clusters */
  listClusters(): Promise<{ clusters: any[] }>;

  /** Discover clusters */
  discoverClusters(options: any): Promise<{ clusters: any[] }>;
}

/**
 * Hook that returns a unified archive API
 *
 * Automatically selects local (Electron) or cloud backend based on platform
 * and connection status. Functions check status at CALL TIME using refs
 * to avoid dependency array instability.
 *
 * STABILITY GUARANTEE: The returned object has stable identity. Callbacks
 * only change when cloudApi changes (rare). Internal routing uses refs.
 */
export function useArchiveApi(): ArchiveApi {
  const cloudApi = useApi();
  const archive = useArchiveOptional();

  // Platform detection (stable - never changes after mount)
  const isElectron = useMemo(
    () => typeof window !== 'undefined' && !!window.isElectron,
    []
  );

  // Use refs for values that change but shouldn't cause callback regeneration
  // This is the key fix: we read current values at CALL TIME, not at callback creation time
  const archiveRef = useRef<{
    status: string | undefined;
    provider: IArchiveProvider | undefined;
  }>({ status: undefined, provider: undefined });

  // Update ref when archive changes (doesn't trigger re-renders of consumers)
  useEffect(() => {
    archiveRef.current = {
      status: archive?.status,
      provider: archive?.provider,
    };
  }, [archive?.status, archive?.provider]);

  // Helper to check if local provider should be used (reads from ref at call time)
  const shouldUseLocal = useCallback(() => {
    return isElectron && archiveRef.current.status === 'connected' && !!archiveRef.current.provider;
  }, [isElectron]);

  // Compute current state for the returned object (these are primitive booleans, safe to recompute)
  const isLocal = isElectron && archive?.status === 'connected' && !!archive?.provider;
  const isAvailable = isLocal || !isElectron;

  // Create functions that check status at call time via ref
  // Dependencies only include stable values (isElectron, cloudApi)
  const getSources = useCallback(async () => {
    if (shouldUseLocal() && archiveRef.current.provider) {
      return archiveRef.current.provider.getSources();
    }
    return cloudApi.archive.getSources();
  }, [shouldUseLocal, cloudApi]);

  const getThreads = useCallback(async (options?: any) => {
    if (shouldUseLocal() && archiveRef.current.provider) {
      return archiveRef.current.provider.getThreads(options);
    }
    return cloudApi.archive.getThreads(options);
  }, [shouldUseLocal, cloudApi]);

  const getContentProfile = useCallback(async (): Promise<{ formats: Record<string, number>; hasMedia: number; total: number }> => {
    if (shouldUseLocal()) {
      if (window.electronArchive?.query) {
        return window.electronArchive.query('getContentProfile', []) as Promise<{ formats: Record<string, number>; hasMedia: number; total: number }>;
      }
      return { formats: {}, hasMedia: 0, total: 0 };
    }
    // Cloud: not implemented yet
    return { formats: {}, hasMedia: 0, total: 0 };
  }, [shouldUseLocal]);

  const getThread = useCallback(async (threadId: string) => {
    if (shouldUseLocal() && archiveRef.current.provider) {
      return archiveRef.current.provider.getThread(threadId);
    }
    return cloudApi.archive.getThread(threadId);
  }, [shouldUseLocal, cloudApi]);

  const getNode = useCallback(async (nodeId: string) => {
    if (shouldUseLocal() && archiveRef.current.provider) {
      return archiveRef.current.provider.getNode(nodeId);
    }
    return cloudApi.archive.getNode(nodeId);
  }, [shouldUseLocal, cloudApi]);

  const getStats = useCallback(async () => {
    if (shouldUseLocal() && archiveRef.current.provider) {
      return archiveRef.current.provider.getStats();
    }
    return cloudApi.archive.getStats();
  }, [shouldUseLocal, cloudApi]);

  // Search - routes to local or cloud (keyword search)
  const search = useCallback(async (sessionId: string, query: string, options?: any): Promise<{ results: SearchResult[] }> => {
    if (shouldUseLocal() && archiveRef.current.provider?.search) {
      const response = await archiveRef.current.provider.search(query, options);
      // Ensure results have all required fields for compatibility
      return {
        results: (response.results || []).map((r: any) => ({
          id: r.id,
          text: r.text,
          source: 'archive' as const,
          score: r.score || 0,
          title: r.title,
          sourceType: r.sourceType,
          wordCount: r.wordCount || 0,
          hierarchyLevel: r.hierarchyLevel || 0,
          scoreBreakdown: r.scoreBreakdown,
          provenance: r.provenance ? {
            ...r.provenance,
            threadTitle: r.provenance.threadTitle,
          } : undefined,
          enrichment: r.enrichment,
        })),
      };
    }
    return cloudApi.search(sessionId, query, options);
  }, [shouldUseLocal, cloudApi]);

  // Semantic search - uses embeddings (pgvector in local, Vectorize in cloud)
  const searchSemantic = useCallback(async (
    query: string,
    options?: { limit?: number; threshold?: number; sourceTypes?: string[] }
  ) => {
    if (isElectron) {
      const electronArchive = window.electronArchive;
      if (electronArchive?.searchSemantic) {
        const r = await electronArchive.searchSemantic(query, options);
        return {
          results: r.results.map((item): SearchResult => ({
            id: item.id,
            text: item.text,
            source: 'archive',
            score: item.score,
            title: item.title,
            sourceType: item.sourceType,
            wordCount: item.wordCount || 0,
            hierarchyLevel: item.hierarchyLevel || 0,
            scoreBreakdown: item.scoreBreakdown,
            provenance: item.provenance ? {
              sourceStore: (item.provenance.sourceStore as 'archive' | 'books') || 'archive',
              sourceType: item.provenance.sourceType || '',
              threadRootId: item.provenance.threadRootId,
              threadTitle: item.provenance.threadTitle,
              authorRole: item.provenance.authorRole,
              uri: item.provenance.uri,
            } : undefined,
          })),
          count: r.count,
        };
      }
      throw new Error('Semantic search requires Ollama for embedding generation. Start Ollama.');
    }
    // Cloud API semantic search
    return cloudApi.search('semantic', query, {
      ...options,
      sources: options?.sourceTypes,
    });
  }, [isElectron, cloudApi]);

  // Find similar content by text
  const findSimilar = useCallback(async (
    text: string,
    options?: { limit?: number; threshold?: number }
  ) => {
    if (isElectron) {
      const electronArchive = window.electronArchive;
      if (electronArchive?.findSimilar) {
        const r = await electronArchive.findSimilar(text, options);
        return {
          results: r.results.map((item): SearchResult => ({
            id: item.id,
            text: item.text,
            source: 'archive',
            score: item.score,
            title: item.title,
            sourceType: item.sourceType,
            wordCount: item.wordCount || 0,
            hierarchyLevel: item.hierarchyLevel || 0,
            scoreBreakdown: item.scoreBreakdown,
            provenance: item.provenance ? {
              sourceStore: (item.provenance.sourceStore as 'archive' | 'books') || 'archive',
              sourceType: item.provenance.sourceType || '',
              threadRootId: item.provenance.threadRootId,
              threadTitle: item.provenance.threadTitle,
              authorRole: item.provenance.authorRole,
              uri: item.provenance.uri,
            } : undefined,
          })),
          count: r.count,
        };
      }
      throw new Error('Similarity search requires Ollama for embedding generation. Start Ollama.');
    }
    return cloudApi.findSimilar('similarity', text, options);
  }, [isElectron, cloudApi]);

  // Server-side proximity-weighted search for browse panel
  const searchBrowse = useCallback(async (query: string, options?: any): Promise<{ results: ArchiveThread[]; total: number }> => {
    if (shouldUseLocal()) {
      // Route through IPC via archive:query with method 'searchBrowse'
      if (window.electronArchive?.query) {
        return window.electronArchive.query('searchBrowse', [query, options || {}]) as Promise<{ results: ArchiveThread[]; total: number }>;
      }
      // Fallback: return empty
      return { results: [], total: 0 };
    }
    // Cloud: not implemented yet, fallback to empty
    return { results: [], total: 0 };
  }, [shouldUseLocal]);

  // Random embedded node for drift
  const randomNode = useCallback(async () => {
    if (isElectron) {
      const electronArchive = window.electronArchive;
      if (electronArchive?.randomNode) {
        return electronArchive.randomNode();
      }
      return null;
    }
    // Cloud doesn't support randomNode yet
    return null;
  }, [isElectron]);

  // Hybrid search — agentic search combining keyword + semantic
  const searchHybrid = useCallback(async (
    query: string,
    options?: { limit?: number; sourceTypes?: string[]; mode?: string }
  ): Promise<{ results: SearchResult[]; count: number; stats?: any }> => {
    if (isElectron) {
      const electronArchive = window.electronArchive;
      if (electronArchive?.agenticSearch) {
        const r = await electronArchive.agenticSearch(query, options);
        return {
          results: r.results.map((item): SearchResult => ({
            id: item.id,
            text: item.text,
            source: 'archive',
            score: item.score,
            title: item.title,
            sourceType: item.sourceType,
            wordCount: item.wordCount || 0,
            hierarchyLevel: item.hierarchyLevel || 0,
            scoreBreakdown: item.scoreBreakdown,
            provenance: item.provenance ? {
              sourceStore: (item.provenance.sourceStore as 'archive' | 'books') || 'archive',
              sourceType: item.provenance.sourceType || '',
              threadRootId: item.provenance.threadRootId,
              threadTitle: item.provenance.threadTitle,
              authorRole: item.provenance.authorRole,
              uri: item.provenance.uri,
            } : undefined,
            enrichment: item.enrichment,
          })),
          count: r.count,
          stats: r.stats,
        };
      }
      // Fallback to keyword search if Ollama is unavailable
      if (shouldUseLocal() && archiveRef.current.provider?.search) {
        const response = await archiveRef.current.provider.search(query, options);
        return {
          results: (response.results || []).map((r: any) => ({
            id: r.id,
            text: r.text,
            source: 'archive' as const,
            score: r.score || 0,
            title: r.title,
            sourceType: r.sourceType,
            wordCount: r.wordCount || 0,
            hierarchyLevel: r.hierarchyLevel || 0,
            scoreBreakdown: r.scoreBreakdown,
            provenance: r.provenance,
            enrichment: r.enrichment,
          })),
          count: response.results?.length || 0,
        };
      }
    }
    // Cloud: use existing search with hybrid mode
    return cloudApi.search('hybrid', query, {
      ...options,
      sources: options?.sourceTypes,
    });
  }, [isElectron, shouldUseLocal, cloudApi]);

  // Clustering - now supports local via pgvector
  const listClusters = useCallback(async () => {
    if (isElectron) {
      const electronArchive = window.electronArchive;
      if (electronArchive?.listClusters) {
        return electronArchive.listClusters();
      }
      throw new Error('Clustering requires PostgreSQL with pgvector. Check database connection.');
    }
    return cloudApi.listClusters();
  }, [isElectron, cloudApi]);

  const discoverClusters = useCallback(async (options: any) => {
    if (isElectron) {
      const electronArchive = window.electronArchive;
      if (electronArchive?.discoverClusters) {
        return electronArchive.discoverClusters(options);
      }
      throw new Error('Clustering requires PostgreSQL with pgvector. Check database connection.');
    }
    return cloudApi.discoverClusters(options);
  }, [isElectron, cloudApi]);

  // Memoize the returned object to ensure stable identity
  // Only recreate when the callbacks or status booleans actually change
  return useMemo(() => ({
    isAvailable,
    isLocal,
    getSources,
    getThreads,
    getContentProfile,
    getThread,
    getNode,
    getStats,
    search,
    searchBrowse,
    searchHybrid,
    searchSemantic,
    findSimilar,
    randomNode,
    listClusters,
    discoverClusters,
  }), [
    isAvailable,
    isLocal,
    getSources,
    getThreads,
    getContentProfile,
    getThread,
    getNode,
    getStats,
    search,
    searchBrowse,
    searchHybrid,
    searchSemantic,
    findSimilar,
    randomNode,
    listClusters,
    discoverClusters,
  ]);
}

export default useArchiveApi;
